// Typed fetch client for the Fajr Arabi API — consumer app (apps/app).
// Mirrors apps/admin/src/lib/api.ts so both apps share one shape. The SDK
// package will absorb both later.

const STAGING_API = 'https://staging-api.fajrarabi.com';
const PROD_API = 'https://api.fajrarabi.com';

function defaultApiBase(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.fajrarabi.com') return PROD_API;
    if (host === 'staging-app.fajrarabi.com') return STAGING_API;
  }
  // Local dev — hit staging directly.
  return STAGING_API;
}

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? defaultApiBase();

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

// M1 (motiflink §7.5): silent-refresh-on-401. The 2h access JWT dies quietly;
// one deduped POST /auth/refresh rotates the 30d refresh token and the
// original request is retried once. A user holding only a live refresh cookie
// is transparently re-authenticated on their first API call.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function rawRequest(path: string, opts: ApiRequestOptions): Promise<Response> {
  const { method = 'GET', body, signal } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';

  return fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

// Auth endpoints where a 401 is the answer, not a stale-token symptom.
const NO_REFRESH_PATHS = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

export async function api<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  let resp = await rawRequest(path, opts);

  if (resp.status === 401 && !NO_REFRESH_PATHS.has(path)) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      resp = await rawRequest(path, opts);
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const parsed: unknown = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const e = (parsed as { error?: { code: string; message: string; details?: unknown } })?.error;
    throw new ApiError(
      resp.status,
      e?.code ?? 'unknown',
      e?.message ?? `Request failed (${resp.status})`,
      e?.details,
    );
  }

  return parsed as T;
}
